/**
 * PdfDropzone — the operator's "drop a document to start a run" affordance.
 *
 * DRY: the drop AFFORDANCE reuses ``Composer.DropOverlay`` (the same dashed
 * cream overlay the chat composer paints while dragging). The drag COUNTER
 * pattern (enter/leave depth so nested children don't re-fire) mirrors
 * ``useChatAttachments.getDragHandlers`` — but we can't reuse that hook here
 * because it uploads straight into the chat attachment buckets; the run
 * surface needs the raw ``File`` to hand to ``runFlowWithFile`` (one call that
 * uploads + creates the content_item + starts the run). So this owns a tiny
 * handler set and simply calls back with the File.
 */

import { Composer, cn, toast } from "@ando/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import { CornerDownLeft, FileUp } from "lucide-react";
import * as React from "react";

/**
 * Stable slugs of the trigger's ``accepted_types`` config — the BE contract
 * (``node_registry.TRIGGER_FILE_ACCEPT_CATEGORIES``). Absent/unknown = "tutti"
 * = the platform attachment allowlist, enforced server-side (the 400 carries
 * the accepted list); the client-side gate here is UX, not security.
 */
export const ACCEPTED_TYPE_SLUGS = ["tutti", "documenti", "pdf", "testo", "audio"] as const;
export type AcceptedTypesSlug = (typeof ACCEPTED_TYPE_SLUGS)[number];

const DOC_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".odt",
  ".ods",
  ".odp",
  ".rtf",
] as const;

interface AcceptSpec {
  /** ``accept`` attr for the file input (undefined = no client restriction). */
  readonly attr: string | undefined;
  readonly matches: (file: File) => boolean;
}

const hasExt = (file: File, exts: readonly string[]): boolean => {
  const name = file.name.toLowerCase();
  return exts.some((ext) => name.endsWith(ext));
};

const ACCEPT_SPECS: Record<AcceptedTypesSlug, AcceptSpec> = {
  // "tutti" defers entirely to the BE allowlist — a wrong type gets the 400
  // with the accepted list, surfaced by the caller's error toast.
  tutti: { attr: undefined, matches: () => true },
  documenti: {
    attr: DOC_EXTENSIONS.join(","),
    matches: (f) => hasExt(f, DOC_EXTENSIONS) || f.type === "application/pdf",
  },
  pdf: {
    attr: "application/pdf,.pdf",
    matches: (f) => f.type === "application/pdf" || hasExt(f, [".pdf"]),
  },
  testo: {
    attr: "text/*,.txt,.csv,.md",
    // Estensioni come fallback (specchio di ``documenti``): un .md trascinato
    // spesso arriva SENZA MIME — il solo prefisso "text/" lo rifiuterebbe
    // lato client mentre il BE lo accetta.
    matches: (f) => f.type.startsWith("text/") || hasExt(f, [".txt", ".csv", ".md"]),
  },
  audio: { attr: "audio/*", matches: (f) => f.type.startsWith("audio/") },
};

export interface PdfDropzoneProps {
  /** Called with the picked/dropped file (the first one when ``multiple``). */
  readonly onFile: (file: File) => void;
  /**
   * Multi-file mode (Sep 2026, "template + N sources" flows): every accepted
   * file of one pick/drop is delivered at once via ``onFiles``; ``onFile`` is
   * NOT called. Files that fail the accept spec are skipped with one toast.
   */
  readonly multiple?: boolean;
  readonly onFiles?: (files: File[]) => void;
  /** Upload/run in flight — disables the control + shows the busy label. */
  readonly busy?: boolean;
  /**
   * The flow trigger's ``accepted_types`` slug (config-driven, like the SOTA
   * form-trigger patterns). Defaults to "pdf" ONLY when omitted by legacy
   * callers; the run surface passes the flow's actual config ("tutti" when the
   * trigger declares nothing, mirroring the BE default).
   */
  readonly acceptedTypes?: AcceptedTypesSlug;
  /** Dense variant for guided launchers that already explain the workflow. */
  readonly compact?: boolean;
}

function acceptSpec(slug: AcceptedTypesSlug | undefined): AcceptSpec {
  return ACCEPT_SPECS[slug ?? "pdf"] ?? ACCEPT_SPECS.tutti;
}

/**
 * Shared client-side accept predicate — the ONE place that answers "does this
 * file match the slug?" (console editors import it instead of re-implementing
 * the pdf check). UX-only: the BE allowlist remains the security gate.
 */
export function isAcceptedFile(file: File, slug: AcceptedTypesSlug = "pdf"): boolean {
  return acceptSpec(slug).matches(file);
}

export function PdfDropzone({
  onFile,
  onFiles,
  multiple = false,
  busy = false,
  acceptedTypes,
  compact = false,
}: PdfDropzoneProps): React.JSX.Element {
  const { t } = useLingui();
  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragDepth = React.useRef(0);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const spec = acceptSpec(acceptedTypes);

  const rejectionMessage = (): string => {
    switch (acceptedTypes ?? "pdf") {
      case "documenti":
        return t`Questo flow accetta solo documenti (PDF, Word, Excel, PowerPoint)`;
      case "testo":
        return t`Questo flow accetta solo file di testo`;
      case "audio":
        return t`Questo flow accetta solo file audio`;
      default:
        return t`Carica un file PDF`;
    }
  };

  const accept = React.useCallback(
    (files: FileList | null) => {
      if (multiple && onFiles) {
        const picked = Array.from(files ?? []);
        if (picked.length === 0) return;
        const ok = picked.filter((f) => spec.matches(f));
        if (ok.length < picked.length) toast.error(rejectionMessage());
        if (ok.length > 0) onFiles(ok);
        return;
      }
      const file = files?.[0];
      if (!file) return;
      if (!spec.matches(file)) {
        toast.error(rejectionMessage());
        return;
      }
      onFile(file);
    },
    // eslint-free deps: rejectionMessage is derived from acceptedTypes+t.
    [onFile, onFiles, multiple, t, acceptedTypes, spec],
  );

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    if (dragDepth.current === 1) setIsDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragOver(false);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setIsDragOver(false);
    if (busy) return;
    accept(e.dataTransfer?.files ?? null);
  };

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className="relative w-full max-w-lg"
    >
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        aria-label={(acceptedTypes ?? "pdf") === "pdf" ? t`Carica un PDF` : t`Carica un documento`}
        className={cn(
          "flex w-full flex-col items-center justify-center rounded-[14px] border border-hairline border-dashed bg-cream/40 text-center transition-colors",
          compact ? "gap-2 px-5 py-7" : "gap-3 px-8 py-14",
          "hover:border-ando-orange/60 hover:bg-cream/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          busy && "cursor-wait opacity-70",
        )}
      >
        <span className="grid h-11 w-11 place-items-center rounded-full bg-ando-orange-soft text-ando-orange">
          <FileUp size={20} aria-hidden strokeWidth={1.75} />
        </span>
        <span className="font-medium text-[13px] text-ink-1">
          {busy ? (
            <Trans>Caricamento…</Trans>
          ) : (acceptedTypes ?? "pdf") === "pdf" ? (
            <Trans>Trascina qui un PDF, o clicca per caricarlo</Trans>
          ) : (
            <Trans>Trascina qui un documento, o clicca per caricarlo</Trans>
          )}
        </span>
        {busy ? null : (
          <>
            <span className={cn("text-[12px] text-ink-3", compact && "hidden sm:inline")}>
              <Trans>Il flow analizza il documento e prepara una proposta da approvare.</Trans>
            </span>
            <span
              aria-hidden
              className={cn(
                "mt-1 items-center gap-1.5 text-[10.5px] text-ink-4",
                compact ? "hidden" : "flex",
              )}
            >
              <kbd className="inline-flex items-center rounded border border-hairline bg-ink-1/[0.03] px-1 py-px font-mono text-[10px] tabular-nums">
                <CornerDownLeft size={10} aria-hidden strokeWidth={1.75} />
              </kbd>
              <Trans>per scegliere un file</Trans>
            </span>
          </>
        )}
      </button>

      <Composer.DropOverlay
        active={isDragOver && !busy}
        label={
          multiple
            ? t`Rilascia per aggiungere alla coda`
            : (acceptedTypes ?? "pdf") === "pdf"
              ? t`Rilascia il PDF per avviare`
              : t`Rilascia il documento per avviare`
        }
      />

      <input
        ref={inputRef}
        type="file"
        accept={spec.attr}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          accept(e.target.files);
          // Reset so picking the same file twice re-fires ``onChange``.
          e.target.value = "";
        }}
      />
    </div>
  );
}
